/**
 * Gallery Navigation Module
 * Adds custom navigation arrows to the product gallery
 * Works with Dawn theme and custom product configurator
 * 
 * FIXED VERSION - Adds robust error handling for mobile slider
 */
class GalleryNavigation {
  constructor() {
    // Find the media list
    this.mediaList = document.querySelector('.product__media-list');
    if (!this.mediaList) {
      console.log('[GalleryNav] Media list not found');
      return;
    }
    
    // Store the media wrapper for positioning context
    this.mediaWrapper = document.querySelector('.product__media-wrapper');
    
    // Use a more robust way to get all media items
    this.mediaItems = Array.from(document.querySelectorAll('.product__media-item'));
    this.thumbnailItems = Array.from(document.querySelectorAll('.thumbnail-list__item'));
    this.thumbnailList = document.querySelector('.thumbnail-list');
    
    // Find the navigation arrows
    this.prevButton = document.querySelector('.gallery-nav-arrow.gallery-nav-prev');
    this.nextButton = document.querySelector('.gallery-nav-arrow.gallery-nav-next');
    
    // If arrows don't exist, create them
    if (!this.prevButton || !this.nextButton) {
      console.log('[GalleryNav] Creating navigation arrows');
      this.createNavigationButtons();
    }
    
    // Flag to prevent double navigation
    this.isNavigating = false;
    
    // Initialize configurator state
    this.configuratorState = {
      active: false,
      eartips: null,
      chestpiece: null,
      mainProduct: null,
      viewingRegularImage: false
    };
    
    this.init();
  }
  
  createNavigationButtons() {
    // Create the container if it doesn't exist
    const navContainer = document.createElement('div');
    navContainer.className = 'gallery-navigation';
    navContainer.style.cssText = `
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      display: flex;
      justify-content: space-between;
      pointer-events: none;
      z-index: 100;
      transform: translateY(-50%);
      padding: 0 15px;
    `;
    
    // Create previous button
    this.prevButton = document.createElement('button');
    this.prevButton.className = 'gallery-nav-arrow gallery-nav-prev';
    this.prevButton.setAttribute('aria-label', 'Previous slide');
    this.prevButton.style.cssText = `
      width: 50px;
      height: 50px;
      border-radius: 50%;
      background-color: rgba(255, 255, 255, 0.9);
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      pointer-events: auto;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
      transition: background-color 0.3s;
      z-index: 100;
    `;
    this.prevButton.innerHTML = `
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 18L9 12L15 6" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    
    // Create next button
    this.nextButton = document.createElement('button');
    this.nextButton.className = 'gallery-nav-arrow gallery-nav-next';
    this.nextButton.setAttribute('aria-label', 'Next slide');
    this.nextButton.style.cssText = `
      width: 50px;
      height: 50px;
      border-radius: 50%;
      background-color: rgba(255, 255, 255, 0.9);
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      pointer-events: auto;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
      transition: background-color 0.3s;
      z-index: 100;
    `;
    this.nextButton.innerHTML = `
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 6L15 12L9 18" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    
    // Add hover effects
    const addHoverStyles = (button) => {
      button.addEventListener('mouseenter', () => {
        button.style.backgroundColor = 'rgba(255, 255, 255, 1)';
      });
      button.addEventListener('mouseleave', () => {
        button.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
      });
    };
    
    addHoverStyles(this.prevButton);
    addHoverStyles(this.nextButton);
    
    // Append buttons to container
    navContainer.appendChild(this.prevButton);
    navContainer.appendChild(this.nextButton);
    
    // Add the container to the media wrapper
    if (this.mediaWrapper) {
      this.mediaWrapper.appendChild(navContainer);
    } else if (this.mediaList) {
      // If no wrapper found, add to the list itself
      this.mediaList.parentNode.appendChild(navContainer);
    }
    
    console.log('[GalleryNav] Navigation buttons created');
  }
  
  init() {
    console.log('[GalleryNav] Initializing');
    
    // Use both click and touchend events with a flag to prevent duplicates
    const addButtonListeners = (button, direction) => {
      // Track if we're currently processing a touch event
      let touchProcessed = false;
      
      // Handle click events (primarily for desktop)
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Skip if this was triggered by a touch that we already processed
        if (touchProcessed) {
          touchProcessed = false;
          return;
        }
        
        if (!this.isNavigating) {
          this.isNavigating = true;
          this.navigate(direction);
          // Allow navigation again after a short delay
          setTimeout(() => {
            this.isNavigating = false;
          }, 300);
        }
      });
      
      // Add specific handling for touch events (mobile)
      button.addEventListener('touchstart', (e) => {
        // Prevent default to avoid any conflicts with other handlers
        e.preventDefault();
        e.stopPropagation();
        
        // Add active state for visual feedback
        button.classList.add('touch-active');
      }, { passive: false });
      
      button.addEventListener('touchend', (e) => {
        // Prevent default to avoid any conflicts
        e.preventDefault();
        e.stopPropagation();
        
        // Remove active state
        button.classList.remove('touch-active');
        
        if (!this.isNavigating) {
          this.isNavigating = true;
          touchProcessed = true; // Mark that we've processed this touch
          this.navigate(direction);
          
          // Allow navigation again after a delay
          setTimeout(() => {
            this.isNavigating = false;
            touchProcessed = false;
          }, 300);
        }
      }, { passive: false });
      
      // Handle touch cancel
      button.addEventListener('touchcancel', (e) => {
        button.classList.remove('touch-active');
        touchProcessed = false;
      });
    };
    
    // Add listeners to both buttons
    addButtonListeners(this.prevButton, 'prev');
    addButtonListeners(this.nextButton, 'next');
    
    // Add keyboard navigation
    if (this.mediaList) {
      this.mediaList.setAttribute('tabindex', '0');
      this.mediaList.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft' && !this.isNavigating) {
          this.isNavigating = true;
          this.navigate('prev');
          setTimeout(() => { this.isNavigating = false; }, 300);
          e.preventDefault();
        } else if (e.key === 'ArrowRight' && !this.isNavigating) {
          this.isNavigating = true;
          this.navigate('next');
          setTimeout(() => { this.isNavigating = false; }, 300);
          e.preventDefault();
        }
      });
    }
    
    // Also add a small adjustment to the button styling for better mobile touch targets
    [this.prevButton, this.nextButton].forEach(button => {
      // Ensure buttons have sufficient touch area on mobile
      const originalStyle = button.getAttribute('style');
      button.setAttribute('style', `${originalStyle} min-width: 44px; min-height: 44px;`);
      
      // Add styles for touch feedback
      const styleEl = document.createElement('style');
      styleEl.textContent = `
        .gallery-nav-arrow.touch-active {
          transform: scale(0.95);
          background-color: rgba(245, 245, 245, 0.95) !important;
        }
      `;
      document.head.appendChild(styleEl);
    });
    
    // Initialize visibility and other components
    setTimeout(() => {
      this.updateArrowVisibility();
    }, 500);
    
    this.setupVisibilityObserver();
    this.setupConfiguratorHooks();
    this.startConfiguratorMonitoring();
    
    console.log('[GalleryNav] Initialized');
  }

  // Helper method for finding and navigating Dawn slider
  findAndNavigateSlider(mediaItem, thumbIndex) {
    // Try multiple approaches to find and manipulate the slider
    console.log('[GalleryNav] Finding and navigating slider to index:', thumbIndex);
    
    // First attempt: find the slider controller
    const slider = document.querySelector('.slider');
    if (!slider) {
      console.log('[GalleryNav] No slider found');
      return false;
    }
    
    // Track if any navigation method succeeded
    let navigationSuccessful = false;
    
    // Method 1: Try using Dawn's slider API if available
    try {
      const sliderComponent = slider.sliderComponent;
      if (sliderComponent && typeof sliderComponent.slideToIndex === 'function') {
        console.log('[GalleryNav] Using Dawn slider API');
        sliderComponent.slideToIndex(thumbIndex);
        navigationSuccessful = true;
      }
    } catch (err) {
      console.log('[GalleryNav] Dawn slider API not available:', err);
    }
    
    // Method 2: Try using the slider's scrollTo method
    if (!navigationSuccessful) {
      try {
        if (typeof slider.scrollTo === 'function') {
          // Identify the correct slide to scroll to
          const slides = slider.querySelectorAll('.product__media-item, .slider__slide');
          if (slides && slides.length > thumbIndex) {
            const targetSlide = slides[thumbIndex];
            if (targetSlide) {
              console.log('[GalleryNav] Using slider.scrollTo');
              slider.scrollTo({
                left: targetSlide.offsetLeft,
                behavior: 'smooth'
              });
              navigationSuccessful = true;
            }
          }
        }
      } catch (err) {
        console.log('[GalleryNav] Slider scrollTo not available:', err);
      }
    }
    
    // Method 3: Manual track manipulation
    if (!navigationSuccessful) {
      try {
        const sliderTrack = slider.querySelector('.slider__track');
        if (sliderTrack) {
          const slides = sliderTrack.querySelectorAll('.product__media-item, .slider__slide');
          if (slides && slides.length > thumbIndex) {
            const targetSlide = slides[thumbIndex];
            if (targetSlide && typeof targetSlide.offsetLeft === 'number') {
              console.log('[GalleryNav] Manually transforming slider track');
              // Set transform directly
              const scrollPosition = targetSlide.offsetLeft;
              sliderTrack.style.transform = `translate3d(-${scrollPosition}px, 0, 0)`;
              sliderTrack.style.transition = 'transform 500ms ease 0s';
              navigationSuccessful = true;
            }
          }
        }
      } catch (err) {
        console.log('[GalleryNav] Manual track transform failed:', err);
      }
    }
    
    // Method 4: Try using data attributes to find the current slide number
    if (!navigationSuccessful) {
      try {
        // Some Dawn versions use data attributes for slides
        const slideNumberInputs = slider.querySelectorAll('[data-index]');
        if (slideNumberInputs.length > 0) {
          const targetInput = Array.from(slideNumberInputs).find(input => 
            parseInt(input.dataset.index) === thumbIndex
          );
          
          if (targetInput) {
            console.log('[GalleryNav] Using slide number input');
            targetInput.checked = true;
            const changeEvent = new Event('change');
            targetInput.dispatchEvent(changeEvent);
            navigationSuccessful = true;
          }
        }
      } catch (err) {
        console.log('[GalleryNav] Slide number input method failed:', err);
      }
    }
    
    // Method 5: Update slider counter and dispatch events
    try {
      const counter = document.querySelector('.slider-counter--current');
      if (counter) {
        console.log('[GalleryNav] Updating slider counter');
        counter.textContent = (thumbIndex + 1).toString();
      }
      
      // Dispatch standard events that the slider might listen for
      const slideEvent = new CustomEvent('slider:slide-change', {
        detail: { index: thumbIndex }
      });
      slider.dispatchEvent(slideEvent);
      
      // Also try a more generic event
      const changeEvent = new CustomEvent('slideChanged', {
        detail: { index: thumbIndex }
      });
      slider.dispatchEvent(changeEvent);
    } catch (err) {
      console.log('[GalleryNav] Event dispatch failed:', err);
    }
    
    return navigationSuccessful;
  }

  // Method to safely update media item visibility with fallbacks
  safelyUpdateMediaItemVisibility(mediaItem) {
    if (!mediaItem) return false;
    
    try {
      // First, directly set style
      mediaItem.style.display = 'block';
      mediaItem.classList.add('is-active');
      
      // Then check if we need to deal with layout containers
      const mediaContainer = mediaItem.closest('.product__media-wrapper, .product-media-container');
      if (mediaContainer) {
        mediaContainer.style.display = 'block';
      }
      
      // If we're in a slider environment
      const sliderSlide = mediaItem.closest('.slider__slide');
      if (sliderSlide) {
        // Make sure the slide is visible
        sliderSlide.style.display = 'block';
        
        // If the slide has aria-hidden, update it
        if (sliderSlide.hasAttribute('aria-hidden')) {
          sliderSlide.setAttribute('aria-hidden', 'false');
        }
      }
      
      // Set tabindex for accessibility
      mediaItem.setAttribute('tabindex', '0');
      
      // Check if it worked
      const isVisible = window.getComputedStyle(mediaItem).display !== 'none';
      return isVisible;
    } catch (err) {
      console.error('[GalleryNav] Error updating media item visibility:', err);
      return false;
    }
  }

  // New version of getVisibleMediaItems
  getVisibleMediaItems() {
    // Refresh the media items array to get the most up-to-date list
    this.mediaItems = Array.from(document.querySelectorAll('.product__media-item'));
    
    return this.mediaItems.filter(item => {
      try {
        const style = window.getComputedStyle(item);
        return style.display !== 'none';
      } catch (err) {
        console.error('[GalleryNav] Error checking media item visibility:', err);
        return false;
      }
    });
  }
  
  getVisibleThumbnails() {
    // Refresh the thumbnails array to get the most up-to-date list
    this.thumbnailItems = Array.from(document.querySelectorAll('.thumbnail-list__item'));
    
    // Get all thumbnails that are currently visible in the DOM
    return this.thumbnailItems.filter(thumb => {
      try {
        const style = window.getComputedStyle(thumb);
        return style.display !== 'none';
      } catch (err) {
        console.error('[GalleryNav] Error checking thumbnail visibility:', err);
        return false;
      }
    });
  }

  getMediaItemFromThumbnail(thumbnail) {
    if (!thumbnail || !thumbnail.dataset || !thumbnail.dataset.target) return null;
    
    const targetId = thumbnail.dataset.target;
    // Refresh media items to ensure we have the latest set
    this.mediaItems = Array.from(document.querySelectorAll('.product__media-item'));
    
    return this.mediaItems.find(item => item.dataset.mediaId === targetId);
  }
  
  navigate(direction) {
    console.log(`[GalleryNav] Navigating ${direction}`);
    
    // Get all visible thumbnails
    const visibleThumbnails = this.getVisibleThumbnails();
    console.log(`[GalleryNav] Found ${visibleThumbnails.length} visible thumbnails`);
    
    if (visibleThumbnails.length <= 1) {
      console.log('[GalleryNav] Not enough visible thumbnails to navigate');
      return;
    }
    
    // Find the currently active thumbnail
    const activeThumb = visibleThumbnails.find(thumb => thumb.classList.contains('is-active'));
    
    if (!activeThumb) {
      console.log('[GalleryNav] No active thumbnail found, activating first visible thumbnail');
      this.activateThumbnail(visibleThumbnails[0]);
      return;
    }
    
    // Find index of active thumbnail in visible items array
    const currentIndex = visibleThumbnails.indexOf(activeThumb);
    console.log(`[GalleryNav] Current active thumbnail index: ${currentIndex}`);
    
    if (currentIndex === -1) {
      console.log('[GalleryNav] Active thumbnail not in visible list. Using first visible thumbnail.');
      this.activateThumbnail(visibleThumbnails[0]);
      return;
    }
    
    // Calculate target index
    let targetIndex;
    if (direction === 'next') {
      targetIndex = (currentIndex + 1) % visibleThumbnails.length;
    } else {
      targetIndex = (currentIndex - 1 + visibleThumbnails.length) % visibleThumbnails.length;
    }
    
    // Get target thumbnail
    const targetThumb = visibleThumbnails[targetIndex];
    if (!targetThumb) {
      console.log('[GalleryNav] Target thumbnail not found');
      return;
    }
    
    console.log(`[GalleryNav] Navigating from thumbnail ${currentIndex} to ${targetIndex}`);
    
    // Save state before navigation
    this.updateConfiguratorState();
    
    // Activate the target thumbnail
    this.activateThumbnail(targetThumb);
  }
  
  // FIXED VERSION of activateThumbnail method
  activateThumbnail(thumbnail) {
    if (!thumbnail) return;
    
    console.log(`[GalleryNav] Activating thumbnail with target: ${thumbnail.dataset.target}`);
    
    // Get the corresponding media item
    const mediaItem = this.getMediaItemFromThumbnail(thumbnail);
    if (!mediaItem) {
      console.log('[GalleryNav] Media item not found for thumbnail');
      return;
    }
    
    // IMPORTANT FIX: Explicitly hide all media items first on mobile
    const isMobileView = window.innerWidth < 750 || !!document.querySelector('.slider');
    if (isMobileView) {
      console.log('[GalleryNav] Mobile view detected, hiding all media items first');
      this.mediaItems.forEach(item => {
        // Hide all items including the active one
        item.style.display = 'none';
        item.classList.remove('is-active');
      });
    }
    
    // Ensure the media item is visible
    mediaItem.style.display = 'block';
    mediaItem.classList.add('is-active');
    
    // Update all thumbnails - remove active class
    this.thumbnailItems.forEach(thumb => {
      thumb.classList.remove('is-active');
    });
    
    // Make the current thumbnail active
    thumbnail.classList.add('is-active');
    
    // Mobile-specific handling
    if (isMobileView) {
      console.log('[GalleryNav] Applying mobile-specific handling');
      
      // Find the thumbnail index
      const allThumbs = Array.from(document.querySelectorAll('.thumbnail-list__item'));
      const visibleThumbs = allThumbs.filter(thumb => {
        try {
          return window.getComputedStyle(thumb).display !== 'none';
        } catch (err) {
          return false;
        }
      });
      const thumbIndex = visibleThumbs.indexOf(thumbnail);
      
      if (thumbIndex !== -1) {
        // First try our robust slider navigation helper
        try {
          const navigationSuccessful = this.findAndNavigateSlider(mediaItem, thumbIndex);
          if (navigationSuccessful) {
            console.log('[GalleryNav] Successfully navigated slider');
          } else {
            console.log('[GalleryNav] Slider navigation unsuccessful, falling back to direct updates');
            // Ensure visibility directly
            this.safelyUpdateMediaItemVisibility(mediaItem);
          }
        } catch (err) {
          console.error('[GalleryNav] Error in slider navigation:', err);
          // Safety fallback
          this.safelyUpdateMediaItemVisibility(mediaItem);
        }
      }
    }
    
    // Wait for DOM changes to settle, then restore configurator state if needed
    setTimeout(() => {
      this.applyConfiguratorState(mediaItem, thumbnail);
      
      // On mobile, make one more attempt to ensure the slide is visible
      if (isMobileView) {
        // Double check the media item is still visible
        if (window.getComputedStyle(mediaItem).display !== 'block') {
          mediaItem.style.display = 'block';
          mediaItem.style.setProperty('display', 'block', 'important');
        }
      }
    }, 50);
  }
  
  // Updated method to consistently track configurator state
  updateConfiguratorState() {
    // Only update if configurator exists and is active
    if (!window.productConfigurator || !window.productConfigurator.isConfiguratorActive) {
      this.configuratorState.active = false;
      return;
    }
    
    this.configuratorState = {
      active: true,
      eartips: window.productConfigurator.currentEartips,
      chestpiece: window.productConfigurator.currentChestpiece,
      mainProduct: window.productConfigurator.currentMainProduct,
      viewingRegularImage: window.productConfigurator.viewingRegularImageInConfigMode
    };
    
    console.log('[GalleryNav] Updated configurator state:', {
      eartips: this.configuratorState.eartips,
      chestpiece: this.configuratorState.chestpiece
    });
  }
  
  applyConfiguratorState(mediaItem, thumbnail) {
    // If no configurator or state, nothing to apply
    if (!this.configuratorState.active || 
        !window.productConfigurator || 
        !window.productConfigurator.isConfiguratorActive) {
      return;
    }
    
    const img = mediaItem?.querySelector('img');
    const altText = img?.alt || '';
    
    // Check if this is a configurator base image
    const isConfigBaseImage = altText.includes('for config_main');
    
    console.log(`[GalleryNav] Applying configurator state to ${isConfigBaseImage ? 'config' : 'regular'} image`);
    
    if (isConfigBaseImage) {
      // For configurator base images, apply overlays
      window.productConfigurator.viewingRegularImageInConfigMode = false;
      
      // Remove existing overlays
      window.productConfigurator.removeAllOverlays();
      
      // Find overlay containers
      const overlayContainer = this.findOverlayContainer(mediaItem);
      
      // Apply overlays to media item and thumbnail
      this.applyOverlays(this.configuratorState, mediaItem, overlayContainer, thumbnail);
      
      // IMPORTANT: Ensure addon config images remain visible
      if (window.productConfigurator.preserveAddonConfigImages) {
        window.productConfigurator.preserveAddonConfigImages();
      }
    } else {
      // For regular images, flag as viewing regular image and ensure overlays are removed
      window.productConfigurator.viewingRegularImageInConfigMode = true;
      window.productConfigurator.removeAllOverlays();
      
      // IMPORTANT: Ensure addon config images remain visible
      if (window.productConfigurator.preserveAddonConfigImages) {
        window.productConfigurator.preserveAddonConfigImages();
      }
    }
  }
  
  applyOverlays(state, mediaItem, overlayContainer, thumbnail) {
    // Apply eartips overlay if needed
    if (state.eartips) {
      // Create overlay on main image
      window.productConfigurator.createOverlay(
        state.eartips, 
        'eartips', 
        mediaItem, 
        overlayContainer
      );
      
      // Create overlay on thumbnail
      window.productConfigurator.createThumbnailOverlay(
        state.eartips, 
        'eartips', 
        thumbnail, 
        thumbnail
      );
    }
    
    // Apply chestpiece overlay if needed
    if (state.chestpiece) {
      // Create overlay on main image
      window.productConfigurator.createOverlay(
        state.chestpiece, 
        'chestpiece', 
        mediaItem, 
        overlayContainer
      );
      
      // Create overlay on thumbnail
      window.productConfigurator.createThumbnailOverlay(
        state.chestpiece, 
        'chestpiece', 
        thumbnail, 
        thumbnail
      );
    }
  }
  
  findOverlayContainer(mediaItem) {
    // First try media wrapper within the item
    let overlayContainer = mediaItem.querySelector('.product__media-wrapper');
    
    // If not found, try direct media element
    if (!overlayContainer) {
      overlayContainer = mediaItem.querySelector('.product__media');
    }
    
    // If still not found, use the media item itself
    if (!overlayContainer) {
      overlayContainer = mediaItem;
    }
    
    return overlayContainer;
  }
  
  updateArrowVisibility() {
    // Get visible thumbnails
    const visibleThumbnails = this.getVisibleThumbnails();
    
    // Show arrows only if we have multiple visible thumbnails
    const shouldShowArrows = visibleThumbnails.length > 1;
    
    console.log(`[GalleryNav] Visibility check: ${visibleThumbnails.length} visible thumbnails, show arrows: ${shouldShowArrows}`);
    
    if (this.prevButton && this.nextButton) {
      this.prevButton.style.display = 'flex';
      this.nextButton.style.display = 'flex';
    }
    
  }
  
  startConfiguratorMonitoring() {
    // Start a monitoring loop to keep track of configurator state
    const monitorInterval = setInterval(() => {
      if (window.productConfigurator && window.productConfigurator.isConfiguratorActive) {
        // Check if state has changed
        const newEartips = window.productConfigurator.currentEartips;
        const newChestpiece = window.productConfigurator.currentChestpiece;
        
        if (newEartips !== this.configuratorState.eartips || 
            newChestpiece !== this.configuratorState.chestpiece) {
          // Update our stored state
          this.updateConfiguratorState();
          
          // Re-apply overlays to active thumbnail if needed
          const activeMediaItem = document.querySelector('.product__media-item.is-active');
          const activeThumb = document.querySelector('.thumbnail-list__item.is-active');
          
          if (activeMediaItem && activeThumb) {
            const img = activeMediaItem.querySelector('img');
            const altText = img?.alt || '';
            
            // Only re-apply for config base images
            if (altText.includes('for config_main')) {
              // Make sure viewingRegularImageInConfigMode is false for config images
              window.productConfigurator.viewingRegularImageInConfigMode = false;
              
              // Re-create thumbnail overlays
              if (newEartips) {
                window.productConfigurator.createThumbnailOverlay(
                  newEartips, 
                  'eartips', 
                  activeThumb, 
                  activeThumb
                );
              }
              
              if (newChestpiece) {
                window.productConfigurator.createThumbnailOverlay(
                  newChestpiece, 
                  'chestpiece', 
                  activeThumb, 
                  activeThumb
                );
              }
            }
          }
        }
      }
    }, 500);
    
    // Store interval id for cleanup
    this.monitorIntervalId = monitorInterval;
  }
  
  setupVisibilityObserver() {
    // Create an observer to watch for changes to the DOM
    if ('MutationObserver' in window) {
      const observer = new MutationObserver(() => {
        // Update arrow visibility when DOM changes
        this.updateArrowVisibility();
      });
      
      // Observe the thumbnail list for changes
      if (this.thumbnailList) {
        observer.observe(this.thumbnailList, { 
          attributes: true,
          childList: true,
          subtree: true
        });
      }
    }
    
    // Handle variant changes
    document.addEventListener('variant:changed', () => {
      console.log('[GalleryNav] Variant changed event detected');
      
      // Allow the DOM to update first
      setTimeout(() => {
        this.updateArrowVisibility();
        
        // If configurator is active, we need to re-apply overlays
        if (window.productConfigurator && window.productConfigurator.isConfiguratorActive) {
          // First update our knowledge of the configurator state
          this.updateConfiguratorState();
          
          // Then process all thumbnails to ensure proper overlays
          const thumbs = this.getVisibleThumbnails();
          const configBaseThumb = thumbs.find(thumb => {
            // Find the config base thumbnail by checking the corresponding media item
            const mediaItem = this.getMediaItemFromThumbnail(thumb);
            if (!mediaItem) return false;
            
            const img = mediaItem.querySelector('img');
            return img && img.alt && img.alt.includes('for config_main');
          });
          
          // If we found the config base thumbnail, ensure it has overlays
          if (configBaseThumb) {
            const mediaItem = this.getMediaItemFromThumbnail(configBaseThumb);
            if (mediaItem) {
              // Set proper flag in configurator
              window.productConfigurator.viewingRegularImageInConfigMode = false;
              
              // Reapply overlays to our found thumbnail and media item
              this.applyConfiguratorState(mediaItem, configBaseThumb);
              
              // If this config thumbnail is active, ensure it's fully active
              if (configBaseThumb.classList.contains('is-active')) {
                // Force active class on media item
                mediaItem.classList.add('is-active');
              }
            }
          }
        }
      }, 100);
    });
    
    // Handle add-on selection changes
    const addonGroups = document.querySelectorAll('.addon-product-options');
    addonGroups.forEach(group => {
      group.addEventListener('change', () => {
        console.log('[GalleryNav] Add-on selection changed');
        
        // Wait for changes to apply
        setTimeout(() => {
          // Update our tracking of configurator state
          this.updateConfiguratorState();
          
          // Ensure arrows show/hide appropriately
          this.updateArrowVisibility();
        }, 100);
      });
    });
  }
  
  setupConfiguratorHooks() {
    // Check for configurator and add hooks if found
    const setupHooks = () => {
      if (window.productConfigurator) {
        // If hooks already added, don't add again
        if (window.productConfigurator._hooksAdded) return true;
        
        // Hook into configurator's updateOverlays method
        const originalUpdateOverlays = window.productConfigurator.updateOverlays;
        window.productConfigurator.updateOverlays = function() {
          // Call the original method first
          originalUpdateOverlays.apply(this, arguments);
          
          // After original method completes, ensure current state is reflected in thumbnails
          setTimeout(() => {
            if (!this.viewingRegularImageInConfigMode) {
              const activeThumb = document.querySelector('.thumbnail-list__item.is-active');
              if (activeThumb) {
                // Re-create thumbnail overlays for both eartips and chestpiece
                if (this.currentEartips) {
                  this.createThumbnailOverlay(
                    this.currentEartips, 
                    'eartips', 
                    activeThumb, 
                    activeThumb
                  );
                }
                
                if (this.currentChestpiece) {
                  this.createThumbnailOverlay(
                    this.currentChestpiece, 
                    'chestpiece', 
                    activeThumb, 
                    activeThumb
                  );
                }
              }
              
              // Apply overlays to all thumbnails that represent config base images
              const thumbnails = document.querySelectorAll('.thumbnail-list__item:not(.is-active)');
              thumbnails.forEach(thumb => {
                // Skip if not visible
                if (window.getComputedStyle(thumb).display === 'none') return;
                
                const mediaItem = document.querySelector(`.product__media-item[data-media-id="${thumb.dataset.target}"]`);
                if (!mediaItem) return;
                
                const img = mediaItem.querySelector('img');
                if (!img || !img.alt) return;
                
                // Only process config base thumbnails
                if (img.alt.includes('for config_main')) {
                  // Apply overlays to this thumbnail
                  if (this.currentEartips) {
                    this.createThumbnailOverlay(
                      this.currentEartips, 
                      'eartips', 
                      thumb, 
                      thumb
                    );
                  }
                  
                  if (this.currentChestpiece) {
                    this.createThumbnailOverlay(
                      this.currentChestpiece, 
                      'chestpiece', 
                      thumb, 
                      thumb
                    );
                  }
                }
              });
            }
            
            // IMPORTANT: Always preserve addon config images
            if (this.preserveAddonConfigImages) {
              this.preserveAddonConfigImages();
            }
          }, 50);
        };
        
        // Mark that hooks have been added
        window.productConfigurator._hooksAdded = true;
        console.log('[GalleryNav] Added hooks to product configurator');
        return true;
      }
      return false;
    };
    
    // Try immediately and then after a delay
    if (!setupHooks()) {
      setTimeout(setupHooks, 1000);
    }
  }
}

// Initialize on DOM content loaded
document.addEventListener('DOMContentLoaded', () => {
  console.log('[GalleryNav] DOM Content Loaded');
  
  // Helper function to create the gallery navigation
  const initGalleryNav = () => {
    if (!window.galleryNavigation) {
      console.log('[GalleryNav] Initializing gallery navigation');
      window.galleryNavigation = new GalleryNavigation();
    }
  };
  
  // First attempt at initialization
  initGalleryNav();
  
  // Try again after a delay to ensure DOM is ready
  setTimeout(initGalleryNav, 1000);
  
  // Final attempt after page load
  window.addEventListener('load', () => {
    console.log('[GalleryNav] Window loaded, ensuring navigation is initialized');
    initGalleryNav();
    
    // Update visibility with fresh DOM
    if (window.galleryNavigation) {
      window.galleryNavigation.updateArrowVisibility();
    }
  });
});  